import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import Select from 'react-select';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createRepairStatus,
  deleteRepairStatus,
  getRepairStatuses,
  updateRepairStatus,
} from '../api/repair-statusAPI';
import { fetchData } from '../utils/apiHelpers';
import { getAll } from '../api';

const RepairStatusLayer = () => {
  const userProfile = useUserProfile();
  const [statuses, setStatuses] = useState([]);
  const [colours, setColours] = useState([]);
  const [newStatus, setNewStatus] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const colourOptions = colours.map((c) => ({
    value: c.id,
    label: c.name,
    bootstrapName: c.bootstrapName,
  }));

  const fetchStatuses = () => {
    fetchData(getRepairStatuses, {}, setStatuses);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newStatus.name?.trim()) newErrors.name = 'Status name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchStatuses();
    fetchData(getAll, { apiName: 'colours' }, setColours);
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (statuses.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [statuses]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleEdit = (item) => {
    setNewStatus(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this repair status?')) return;
    try {
      await deleteRepairStatus(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchStatuses();
    } catch (error) {
      console.error('Error deleting repair status:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        name: newStatus.name,
        coloursId: newStatus.coloursId || null,
      };

      if (editingId) {
        await updateRepairStatus(editingId, payload);
      } else {
        await createRepairStatus(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      await fetchStatuses();
      setNewStatus({});
    } catch (error) {
      console.error('Error saving repair status:', error);
    }
  };

  const renderColour = (item) => {
    const colour = item.colours;
    if (!colour) return '';
    return (
      <span>
        <span
          className={`d-inline-block rounded-1 bg-${colour.bootstrapName}`}
          style={{ width: '10px', height: '10px', marginRight: '8px' }}
        />
        {colour.name}
      </span>
    );
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('repair-status:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={() => {
              setNewStatus({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Rotable Repair Status
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Colour</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td>{renderColour(item)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('repair-status:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('repair-status:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Rotable Repair Status' : 'New Rotable Repair Status'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newStatus.name || ''}
                      onChange={(e) => setNewStatus((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Colour</label>
                    <Select
                      options={colourOptions}
                      value={colourOptions.find((option) => option.value === newStatus.coloursId) || null}
                      onChange={(option) => setNewStatus((prev) => ({ ...prev, coloursId: option?.value }))}
                      isClearable
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairStatusLayer;
